import type { ClientDto } from '@fos/shared/contracts/crm';
import type { ProjectDetail } from '@fos/shared/contracts/projects';
import { PROJECT_BUDGET_WARNING_RATIO } from '@fos/shared/labels';
import { CalendarDays, Clock, type LucideIcon, Mail, Receipt, Server, SquareCheckBig, Timer } from 'lucide-react';
import { Link } from 'react-router';
import { firstName, formatDuration, formatMoneyShort, formatMoneyTotal } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import { SectionHeader } from '../../ui/PageHeader';
import { Section } from '../../ui/Section';
import { Stat } from '../../ui/Stat';
import { EmptyState, ErrorState } from '../../ui/States';
import { ObjectHistory } from '../history';
import { ClientTimeline } from '../inbox';
import { ClientMessages } from '../mail';
import { ClientMaintenanceTab } from '../maintenance';
import { TOUCH_CHILD_BUTTON, TOUCH_TEXT } from '../mobile';
import { formatHours, projectHoursText } from '../projects';
import { NewTaskButton, TaskRows } from '../tasks';
import { AddTimeButton, TimeEntryRows } from '../time';

/**
 * Zakładki profilu klienta i szczegółów projektu: Zadania, Utrzymanie, Historia (Fala 1), Czas (Fala 2: wpisy
 * klienta / projektu z „Dodaj czas” i wykorzystaniem budżetu godzin projektu); Spotkania, Wiedza i Oferty z modułów
 * Fali 2 (rejestracja w `index.tsx`); Wiadomości — wątki klienta z modułu poczty (Fala 4).
 */

const isArchived = (c: ClientDto) => c.status === 'archived' || c.archivedAt !== null;

const linkClasses = `rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`;

/** Pusty stan zakładki modułu z kolejnej fali. */
export function soonTab(icon: LucideIcon, description: string) {
  return function SoonTab() {
    return (
      <div className="rounded-xl border border-dashed border-line">
        <EmptyState icon={icon} title="Pojawi się w kolejnej wersji" description={description} />
      </div>
    );
  };
}

// ── Wiadomości (Fala 4) ─────────────────────────────────────────────────────

/**
 * Zakładka „Wiadomości” klienta: wątki poczty klienta (stan skrzynki, lista, podgląd wątku z odpowiedzią) i „Nowa
 * wiadomość” (szkic → Akceptacje). Moduł Inbox; klient w trybie częściowym (dostęp tylko przez projekt) nie ma
 * korespondencji — zakres wiadomości to cały klient.
 */
export function ClientMessagesTab({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.can('inbox'))
    return (
      <Section>
        <EmptyState icon={Mail} title="Brak dostępu do modułu Inbox" />
      </Section>
    );
  if (client.partial) {
    return (
      <Section>
        <EmptyState
          icon={Mail}
          title="Wiadomości klienta są niedostępne"
          description="Masz dostęp tylko do projektu tego klienta, a korespondencja dotyczy całego klienta."
        />
      </Section>
    );
  }
  return <ClientMessages clientId={client.id} />;
}

// ── Czas (Fala 2) ───────────────────────────────────────────────────────────

/**
 * Zakładka „Czas” klienta: wszystkie wpisy klienta w zakresie osoby (niezależnie od przełącznika Moje / Zespół,
 * jak kafelek „Godziny w miesiącu”), „Dodaj czas” z klientem i link do raportu z filtrem klienta.
 */
export function ClientTimeTab({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.can('time'))
    return (
      <Section>
        <EmptyState icon={Timer} title="Brak dostępu do modułu Czas" />
      </Section>
    );
  return (
    <Section aria-label="Czas klienta">
      <SectionHeader
        title="Wpisy czasu"
        action={
          <div className="flex items-center gap-4">
            <Link to={`/czas?widok=raport&klient=${client.id}`} className={linkClasses}>
              Raport
            </Link>
            {!isArchived(client) && <AddTimeButton defaultClientId={client.id} size="s" />}
          </div>
        }
      />
      <TimeEntryRows
        filter={{ clientId: client.id, scope: 'team' }}
        emptyDescription="Dodaj pierwszy wpis dla tego klienta: opis, projekt, godziny od–do."
      />
    </Section>
  );
}

/**
 * Wykorzystanie czasu projektu (`time.projectUsage`): budżet godzin (kolor ostrzegawczy od 80%, czerwony ponad
 * budżetem), bieżący miesiąc z osobami, wartość pracy (z „Widzi stawki i kwoty”) albo czas rozliczany.
 */
function ProjectTimeSummary({ project }: { project: ProjectDetail }) {
  const access = useAccess();
  const query = trpc.time.projectUsage.useQuery({ projectId: project.id });
  if (query.isError) {
    return (
      <Section>
        <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
      </Section>
    );
  }
  const u = query.data;
  const used = u?.totals.seconds ?? 0;
  const budget = u?.budgetSeconds ?? project.hoursBudgetSeconds ?? null;
  const ratio = budget ? used / budget : 0;
  const tone = ratio > 1 ? 'danger' : ratio >= PROJECT_BUDGET_WARNING_RATIO ? 'warning' : 'neutral';
  const left = budget ? budget - used : null;
  const people = (u?.people ?? []).slice(0, 3).map((p) => `${firstName(p.name)} ${formatHours(p.seconds)} h`);
  const showMoney = access.flag('seesRates') && u?.totals.amount !== undefined;
  const loading = '…';
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
      <Stat
        label="Budżet godzin"
        icon={Clock}
        value={u ? (projectHoursText(used, budget) ?? '0 h') : loading}
        progress={u && budget ? Math.min(1, ratio) : undefined}
        tone={tone}
        showDot={false}
        note={
          budget && left !== null
            ? left >= 0
              ? `zostało ${formatHours(left)} h`
              : `ponad budżet o ${formatHours(-left)} h`
            : 'bez budżetu godzin'
        }
      />
      <Stat
        label="W tym miesiącu"
        icon={CalendarDays}
        value={u ? formatDuration(u.currentMonthSeconds) : loading}
        showDot={false}
        note={people.length > 0 ? people.join(' · ') : 'brak wpisów'}
      />
      {showMoney ? (
        <Stat
          label="Wartość pracy"
          icon={Receipt}
          value={
            u?.totals.amount !== undefined
              ? formatMoneyTotal(
                  {
                    amount: u.totals.amount,
                    currency: u.totals.currency,
                    otherAmounts: (u.totals.otherCurrencies ?? []).map((o) => ({
                      currency: o.currency,
                      amount: o.amount,
                    })),
                  },
                  { short: true },
                )
              : formatMoneyShort('0')
          }
          showDot={false}
          note={`rozliczany ${formatDuration(u?.totals.billableSeconds ?? 0)}`}
        />
      ) : (
        <Stat
          label="Rozliczany czas"
          icon={Receipt}
          value={u ? formatDuration(u.totals.billableSeconds) : loading}
          showDot={false}
          note={u ? `nierozliczany ${formatDuration(u.totals.nonBillableSeconds)}` : 'wczytywanie…'}
        />
      )}
    </div>
  );
}

/** Zakładka „Czas” projektu: wykorzystanie budżetu, wpisy projektu i „Dodaj czas” z projektem. */
export function ProjectTimeTab({ project }: { project: ProjectDetail }) {
  const access = useAccess();
  if (!access.can('time'))
    return (
      <Section>
        <EmptyState icon={Timer} title="Brak dostępu do modułu Czas" />
      </Section>
    );
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <ProjectTimeSummary project={project} />
      <Section aria-label="Wpisy czasu projektu">
        <SectionHeader
          title="Wpisy czasu"
          action={
            <div className="flex items-center gap-4">
              <Link to={`/czas?widok=raport&projekt=${project.id}`} className={linkClasses}>
                Raport
              </Link>
              {!project.archivedAt && (
                <AddTimeButton defaultClientId={project.clientId} defaultProjectId={project.id} size="s" />
              )}
            </div>
          }
        />
        <TimeEntryRows
          filter={{ projectId: project.id, scope: 'team' }}
          emptyDescription="Dodaj pierwszy wpis w tym projekcie: opis i godziny od–do."
        />
      </Section>
    </div>
  );
}

// ── Klient ──────────────────────────────────────────────────────────────────

export function ClientTasksTab({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.can('tasks')) {
    return (
      <Section>
        <EmptyState icon={SquareCheckBig} title="Brak dostępu do modułu Zadania" />
      </Section>
    );
  }
  const canAdd = !client.partial && !isArchived(client);
  return (
    <Section aria-label="Zadania klienta">
      <SectionHeader
        title="Otwarte zadania"
        action={
          <div className="flex items-center gap-4">
            <Link to={`/zadania?widok=lista&klient=${client.id}`} className={linkClasses}>
              Wszystkie w Zadaniach
            </Link>
            {canAdd && (
              <span className={TOUCH_CHILD_BUTTON}>
                <NewTaskButton defaultClientId={client.id} size="s" />
              </span>
            )}
          </div>
        }
      />
      <TaskRows filter={{ clientId: client.id }} limit={50} allowAdd={false} />
    </Section>
  );
}

export function ClientMaintenance({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.can('maintenance')) {
    return (
      <Section>
        <EmptyState icon={Server} title="Brak dostępu do modułu Utrzymanie" />
      </Section>
    );
  }
  return <ClientMaintenanceTab clientId={client.id} />;
}

/**
 * Historia klienta (kompozycja Fali 3): „Zdarzenia” — wiadomości, alarmy, wysłane przypomnienia, podziękowania
 * (Inbox: `inbox.clientTimeline`, także FYI; MODULE_UTRZYMANIE §7 nr 1: mail o płatności „trafia do historii”) —
 * oraz „Zmiany w danych” (audyt rekordu klienta i kontaktów).
 */
export function ClientHistoryTab({ client }: { client: ClientDto }) {
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <Section aria-labelledby="client-history-events">
        <SectionHeader title={<span id="client-history-events">Zdarzenia</span>} />
        <ClientTimeline clientId={client.id} />
      </Section>
      <Section aria-labelledby="client-history-changes">
        <SectionHeader title={<span id="client-history-changes">Zmiany w danych</span>} />
        <ObjectHistory objectType="client" objectId={client.id} refreshKey={client} />
      </Section>
    </div>
  );
}

// ── Projekt ─────────────────────────────────────────────────────────────────

export function ProjectOpenTasks({ project }: { project: ProjectDetail }) {
  const access = useAccess();
  if (!access.can('tasks')) return null;
  return (
    <Section aria-labelledby="project-open-tasks">
      <SectionHeader
        tone="strong"
        title={<span id="project-open-tasks">Otwarte zadania</span>}
        action={
          <Link to={`/zadania?widok=lista&projekt=${project.id}`} className={linkClasses}>
            Wszystkie
          </Link>
        }
      />
      <TaskRows filter={{ projectId: project.id }} limit={5} allowAdd={!project.archivedAt} />
    </Section>
  );
}

export function ProjectTasksTab({ project }: { project: ProjectDetail }) {
  const access = useAccess();
  if (!access.can('tasks')) {
    return (
      <Section>
        <EmptyState icon={SquareCheckBig} title="Brak dostępu do modułu Zadania" />
      </Section>
    );
  }
  return (
    <Section aria-label="Zadania projektu">
      <SectionHeader
        title="Otwarte zadania"
        action={
          <div className="flex items-center gap-4">
            <Link to={`/zadania?widok=lista&projekt=${project.id}`} className={linkClasses}>
              Wszystkie w Zadaniach
            </Link>
            {!project.archivedAt && (
              <span className={TOUCH_CHILD_BUTTON}>
                <NewTaskButton defaultClientId={project.clientId} defaultProjectId={project.id} size="s" />
              </span>
            )}
          </div>
        }
      />
      <TaskRows filter={{ projectId: project.id }} limit={50} allowAdd={false} />
    </Section>
  );
}

export function ProjectHistoryTab({ project }: { project: ProjectDetail }) {
  return (
    <Section>
      <ObjectHistory objectType="project" objectId={project.id} refreshKey={project} />
    </Section>
  );
}
